import { Check, Download, HardDrive, PackageOpen, Smartphone, Trash2, Wifi, WifiOff } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { OfflinePackId } from '../../domain/tools/types';
import { localize } from '../../domain/tools/types';
import { useLanguage } from '../../features/language/useLanguage';
import { clearPackCache, clearTemporaryTaskData } from '../../features/offline/packManager';
import { useOfflinePacks, useStorageEstimate } from '../../features/offline/useOfflinePacks';
import { usePwa } from '../../features/pwa/PwaProvider';
import { formatBytes, formatPercent } from '../../lib/format';

export function OfflineStoragePage() {
  const { t } = useTranslation();
  const { language } = useLanguage();
  const { online, offlineReady, installAvailable, installed: appInstalled, install: installApp } = usePwa();
  const { manifest, installed, loading, error, progress, install, installEverything, remove, refresh } = useOfflinePacks();
  const estimate = useStorageEstimate();
  const [clearing, setClearing] = useState(false);
  const [message, setMessage] = useState<string>();
  const usageRatio = estimate.usage && estimate.quota ? estimate.usage / estimate.quota : 0;
  const progressRatio = progress?.total ? progress.loaded / progress.total : 0;
  const progressName = useMemo(() => manifest?.packs.find((pack) => pack.id === progress?.packId)?.name[language], [manifest, progress, language]);

  const clearOptionalData = async (): Promise<void> => {
    setClearing(true);
    setMessage(undefined);
    try {
      await Promise.all([clearPackCache(), clearTemporaryTaskData()]);
      await refresh();
      setMessage(t('offline.storage.cleared'));
    } finally {
      setClearing(false);
    }
  };

  return (
    <>
      <section className="settings-section">
        <h2>{t('offline.connectivity.title')}</h2>
        <div className="setting-row">
          <div><strong>{online ? t('offline.connectivity.online') : t('offline.connectivity.offline')}</strong><small>{online ? t('offline.connectivity.onlineDescription') : t('offline.connectivity.offlineDescription')}</small></div>
          <span className="status-pill" data-tone={online ? 'positive' : 'attention'}>{online ? <Wifi size={15} /> : <WifiOff size={15} />}{online ? t('offline.connectivity.online') : t('offline.connectivity.offline')}</span>
        </div>
        <div className="setting-row">
          <div><strong>{t('offline.readiness.title')}</strong><small>{offlineReady ? t('offline.readiness.coreReady') : t('offline.readiness.notReadyDescription')}</small></div>
          <span className="status-pill" data-tone={offlineReady ? 'positive' : 'attention'}>{offlineReady ? t('offline.readiness.ready') : t('offline.readiness.notReady')}</span>
        </div>
        <div className="setting-row">
          <div><strong>{t('offline.installApp.title')}</strong><small>{t('offline.installApp.description')}</small></div>
          <button className="button secondary" type="button" disabled={!installAvailable || appInstalled} onClick={() => void installApp()}><Smartphone size={17} />{appInstalled ? t('offline.installApp.installed') : installAvailable ? t('offline.installApp.action') : t('offline.installApp.unavailable')}</button>
        </div>
      </section>

      <section className="settings-section">
        <h2>{t('offline.packs.title')}</h2>
        <p>{t('offline.packs.description')}</p>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '.6rem', marginBlockEnd: '1.2rem' }}>
          <button className="button" type="button" disabled={loading || !manifest} onClick={() => void installEverything()}><Download size={17} />{t('offline.packs.downloadAll')}</button>
          <button className="button secondary" type="button" disabled={loading || installed.size === 0} onClick={() => void clearOptionalData()}><Trash2 size={17} />{t('offline.packs.removeAll')}</button>
        </div>
        {progress && (
          <div className="storage-meter" aria-live="polite">
            <div className="storage-labels"><span>{t('offline.packs.installProgress', { name: progressName ?? progress.packId, percent: Math.round(progressRatio * 100) })}</span><span>{formatBytes(progress.loaded, language)} / {formatBytes(progress.total, language)}</span></div>
            <div className="progress-track" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(progressRatio * 100)}><div className="progress-bar" style={{ width: `${Math.min(100, progressRatio * 100)}%` }} /></div>
          </div>
        )}
        {error && <div className="notice danger" role="alert">{t('errors.offline.packDownloadFailed')} <span dir="auto">{error.message}</span></div>}
        <div className="pack-grid" style={{ marginBlockStart: '1.2rem' }}>
          {manifest?.packs.map((pack) => {
            const current = installed.get(pack.id);
            const ready = current?.version === pack.version;
            return (
              <article className="pack-card" key={pack.id}>
                <div>
                  <div className="pack-card-header">
                    <span className="tool-icon">{pack.kind === 'ocr' ? <PackageOpen size={19} /> : <HardDrive size={19} />}</span>
                    <span className="status-pill" data-tone={ready ? 'positive' : 'attention'}>{ready && <Check size={13} />}{ready ? t('common.status.installed') : t('common.status.notInstalled')}</span>
                  </div>
                  <h3 style={{ marginBlockStart: '.9rem' }}>{localize(pack.name, language)}</h3>
                  <p>{localize(pack.description, language)}</p>
                  <ul className="pack-features" style={{ marginBlockStart: '.8rem' }}>{pack.features[language].map((feature) => <li key={feature}>{feature}</li>)}</ul>
                  <p>{t('offline.packs.deviceRequirements')}: {localize(pack.requirements, language)}</p>
                </div>
                <div className="pack-card-footer">
                  <span className="pack-size">{t('offline.packs.downloadSize')}: {formatBytes(pack.downloadSize, language)}</span>
                  {ready ? (
                    <button className="button secondary" type="button" disabled={loading} onClick={() => void remove(pack.id)}>{t('common.actions.remove')}</button>
                  ) : (
                    <button className="button" type="button" disabled={loading || !pack.available} onClick={() => void install(pack.id as OfflinePackId)}>{t('common.actions.install')}</button>
                  )}
                </div>
              </article>
            );
          })}
        </div>
      </section>

      <section className="settings-section">
        <h2>{t('offline.storage.title')}</h2>
        <p>{t('offline.storage.description')}</p>
        <div className="storage-meter">
          <div className="storage-labels">
            <span>{estimate.loading ? t('offline.storage.estimating') : estimate.usage !== undefined ? formatBytes(estimate.usage, language) : t('offline.storage.unavailable')}</span>
            <span>{estimate.quota ? formatBytes(estimate.quota, language) : ''}</span>
          </div>
          <div className="progress-track" aria-label={t('offline.storage.title')}><div className="progress-bar" style={{ width: `${Math.min(100, usageRatio * 100)}%` }} /></div>
          {estimate.quota && <small>{formatPercent(usageRatio, language)}</small>}
        </div>
        <div className="setting-row" style={{ marginBlockStart: '1.1rem' }}>
          <div><strong>{t('offline.storage.clearModelsAndTemporary')}</strong><small>{t('offline.storage.clearDescription')}</small></div>
          <button className="button danger" type="button" disabled={clearing} onClick={() => void clearOptionalData()}><Trash2 size={17} />{clearing ? t('common.status.loading') : t('common.actions.clear')}</button>
        </div>
        {message && <div className="notice info" role="status">{message}</div>}
      </section>
    </>
  );
}
